<!--
  - SPDX-FileCopyrightText: 2020 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<Question
		v-bind="questionProps"
		:titlePlaceholder="answerType.titlePlaceholder"
		:warningInvalid="answerType.warningInvalid"
		:contentValid="contentValid"
		:shiftDragHandle="shiftDragHandle"
		:errorMessage="errorMessage"
		v-on="commonListeners">
		<template v-if="readOnly">
			<fieldset
				:name="name || undefined"
				:aria-labelledby="titleId"
				:aria-describedby="description ? descriptionId : undefined">
				<table class="answer-grid">
					<thead>
						<tr>
							<th class="first-column"></th>

							<th
								v-for="column in columns"
								:key="column.local ? 'option-local' : column.id">
								{{ column.text }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="row in rows"
							:key="row.local ? 'option-local' : row.id">
							<td class="first-column">{{ row.text }}</td>
							<td
								v-for="column in columns"
								:key="column.local ? 'option-local' : column.id">
								<template v-if="questionType === 'radio'">
									<NcCheckboxRadioSwitch
										:aria-errormessage="
											hasError ? errorId : undefined
										"
										:aria-invalid="hasError ? 'true' : undefined"
										:modelValue="values[row.id]"
										:value="column.id.toString()"
										:name="`${row.id}-answer`"
										type="radio"
										@update:modelValue="
											onChangeCheckboxRadio(row.id, $event)
										" />
								</template>

								<template v-if="questionType === 'checkbox'">
									<NcCheckboxRadioSwitch
										:aria-errormessage="
											hasError ? errorId : undefined
										"
										:aria-invalid="hasError ? 'true' : undefined"
										:modelValue="values[row.id] || []"
										:value="column.id.toString()"
										:name="`${row.id}-answer`"
										type="checkbox"
										@update:modelValue="
											onChangeCheckboxRadio(row.id, $event)
										" />
								</template>

								<template v-if="questionType === 'number'">
									<NcInputField
										type="number"
										:modelValue="plainValues[row.id][column.id]"
										@update:modelValue="
											onChangeTextNumber(
												row.id,
												column.id,
												$event,
											)
										" />
								</template>
							</td>
						</tr>
					</tbody>
				</table>
			</fieldset>
		</template>

		<template v-else>
			<div v-if="isLoading">
				<NcLoadingIcon :size="64" />
			</div>

			<template v-else>
				<div>{{ t('forms', 'Columns') }}</div>
				<Draggable
					v-model="columns"
					class="question__content"
					:animation="300"
					direction="vertical"
					handle=".option__drag-handle"
					invertSwap
					target=".sort-target"
					@update="dirtyOptionsType = 'column'"
					@start="onDragStart"
					@end="onDragEnd">
					<!-- Column input edit -->
					<TransitionGroup
						tag="ul"
						:name="isDragging ? undefined : 'options-list-transition'"
						class="sort-target">
						<AnswerInput
							v-for="(answer, index) in columns"
							:key="answer.local ? 'option-local' : answer.id"
							ref="input"
							:answer="answer"
							:formId="formId"
							:index="index"
							:isUnique="isUnique"
							:maxIndex="columns.length - 2"
							:maxOptionLength="maxStringLengths.optionText"
							optionType="column"
							@createAnswer="onCreateAnswer"
							@update:answer="updateAnswer"
							@delete="deleteOption"
							@focusNext="focusNextInput"
							@moveUp="onOptionMoveUp(index, 'column')"
							@moveDown="onOptionMoveDown(index, 'column')"
							@tabbedOut="checkValidOption('column')" />
					</TransitionGroup>
				</Draggable>

				<div>{{ t('forms', 'Rows') }}</div>
				<Draggable
					v-model="rows"
					class="question__content"
					:animation="300"
					direction="vertical"
					handle=".option__drag-handle"
					invertSwap
					target=".sort-target"
					@update="dirtyOptionsType = 'row'"
					@start="onDragStart"
					@end="onDragEnd">
					<TransitionGroup
						tag="ul"
						:name="isDragging ? undefined : 'options-list-transition'"
						class="sort-target">
						<!-- Row input edit -->
						<AnswerInput
							v-for="(answer, index) in rows"
							:key="answer.local ? 'option-local' : answer.id"
							ref="input"
							:answer="answer"
							:formId="formId"
							:index="index"
							:isUnique="isUnique"
							:maxIndex="rows.length - 2"
							:maxOptionLength="maxStringLengths.optionText"
							optionType="row"
							@createAnswer="onCreateAnswer"
							@update:answer="updateAnswer"
							@delete="deleteOption"
							@focusNext="focusNextInput"
							@moveUp="onOptionMoveUp(index, 'row')"
							@moveDown="onOptionMoveDown(index, 'row')"
							@tabbedOut="checkValidOption('row')" />
					</TransitionGroup>
				</Draggable>
			</template>
		</template>
		<template #insert>
			<slot name="insert" />
		</template>
	</Question>
</template>

<script lang="ts">
import type { FormsOption, GridQuestionValues } from '../../types/Entities.d.ts'

import { n, t } from '@nextcloud/l10n'
import { computed, defineComponent, nextTick, ref } from 'vue'
import { VueDraggable as Draggable } from 'vue-draggable-plus'
import NcCheckboxRadioSwitch from '@nextcloud/vue/components/NcCheckboxRadioSwitch'
import NcInputField from '@nextcloud/vue/components/NcInputField'
import NcLoadingIcon from '@nextcloud/vue/components/NcLoadingIcon'
import AnswerInput from './AnswerInput.vue'
import Question from './Question.vue'
import {
	QUESTION_EMITS,
	QUESTION_PROPS,
	useQuestion,
} from '../../composables/useQuestion.ts'
import {
	QUESTION_MULTIPLE_EMITS,
	useQuestionMultiple,
} from '../../composables/useQuestionMultiple.ts'
import { GridCellType, OptionType } from '../../models/Constants.ts'

type GridQuestionType = GridCellType | 'text'

type GridCellValue = string | number

type GridMatrixValues = Record<number, Record<number, GridCellValue>>

type QuestionGridExtraSettings = {
	optionsLimitMax?: number
	optionsLimitMin?: number
	questionType?: GridQuestionType
}

export default defineComponent({
	name: 'QuestionGrid',

	components: {
		AnswerInput,
		Draggable,
		NcCheckboxRadioSwitch,
		NcInputField,
		NcLoadingIcon,
		Question,
	},

	props: QUESTION_PROPS,
	emits: [...QUESTION_EMITS, ...QUESTION_MULTIPLE_EMITS, 'update:values'],

	setup(props, { emit }) {
		const question = useQuestion(props, { emit })
		const input = ref<
			Array<{
				focus?: () => void
				$props?: { optionType?: string; index?: number }
			} | null>
		>([])
		const isLoading = ref(false)
		const questionMultiple = useQuestionMultiple(props, {
			emit,
			input,
			isLoading,
		})
		const isDragging = ref(false)
		const questionTypes = [
			{ label: t('forms', 'Radio'), id: GridCellType.Radio },
			{ label: t('forms', 'Checkbox'), id: GridCellType.Checkbox },
			{ label: t('forms', 'Number'), id: GridCellType.Number },
			{ label: t('forms', 'Text'), id: 'text' },
		] as Array<{ label: string; id: GridQuestionType }>

		const isUnique = computed(() => {
			return props.answerType?.unique === true
		})

		const values = computed<GridQuestionValues>(() => {
			return (props.values as GridQuestionValues | undefined) ?? {}
		})

		const extraSettings = computed<QuestionGridExtraSettings>(() => {
			return (
				(props.extraSettings as QuestionGridExtraSettings | undefined) ?? {}
			)
		})

		const shiftDragHandle = computed(() => {
			return (
				!props.readOnly
				&& props.options?.length !== 0
				&& !questionMultiple.isLastEmpty.value
			)
		})

		const questionType = computed<GridQuestionType>(() => {
			return extraSettings.value.questionType ?? GridCellType.Radio
		})

		const columns = computed<FormsOption[]>({
			get() {
				return questionMultiple.sortOptionsOfType(
					props.options,
					OptionType.Column,
				)
			},

			set(value: FormsOption[]) {
				questionMultiple.updateOptionsOrder(value, OptionType.Column)
			},
		})

		const rows = computed<FormsOption[]>({
			get() {
				return questionMultiple.sortOptionsOfType(
					props.options,
					OptionType.Row,
				)
			},

			set(value: FormsOption[]) {
				questionMultiple.updateOptionsOrder(value, OptionType.Row)
			},
		})

		const plainValues = computed<GridMatrixValues>(() => {
			const normalizedValues: GridMatrixValues = {}
			for (const row of rows.value) {
				for (const column of columns.value) {
					normalizedValues[row.id] = normalizedValues[row.id] || {}
					const rowValues = values.value[row.id] as
						Record<number, GridCellValue> | undefined
					normalizedValues[row.id][column.id] =
						rowValues?.[column.id] ?? ''
				}
			}

			return normalizedValues
		})

		const validate = async (): Promise<boolean> => {
			const valueCount = Object.keys(values.value).length
			if (props.isRequired && (props.values === null || valueCount === 0)) {
				question.errorMessage.value = t(
					'forms',
					'You must answer this question',
				)
				return false
			}

			if (!isUnique.value) {
				// Validate limits
				const max = extraSettings.value.optionsLimitMax ?? 0
				const min = extraSettings.value.optionsLimitMin ?? 0
				if (max && valueCount > max) {
					question.errorMessage.value = n(
						'forms',
						'You must choose at most one option',
						'You must choose a maximum of %n options',
						max,
					)
					return false
				}
				if (min && valueCount < min) {
					question.errorMessage.value = n(
						'forms',
						'You must choose at least one option',
						'You must choose at least %n options',
						min,
					)
					return false
				}
			}

			question.errorMessage.value = null
			return true
		}

		const onDragStart = (): void => {
			isDragging.value = true
		}

		const onDragEnd = (): void => {
			nextTick(() => {
				isDragging.value = false
			})
		}

		const onChangeCheckboxRadio = (
			rowId: number,
			value: number | number[],
		): void => {
			const nextValues = { ...(values.value as GridQuestionValues) }
			nextValues[rowId] = value

			emit('update:values', nextValues)
		}

		const onChangeTextNumber = (
			rowId: number,
			columnId: number,
			value: GridCellValue,
		): void => {
			const values = { ...plainValues.value }
			values[rowId][columnId] = value

			emit('update:values', values)
		}

		return {
			...question,
			...questionMultiple,
			columns,
			input,
			isDragging,
			isLoading,
			isUnique,
			onChangeCheckboxRadio,
			onChangeTextNumber,
			onDragEnd,
			onDragStart,
			OptionType,
			plainValues,
			questionType,
			questionTypes,
			rows,
			shiftDragHandle,
			t,
			validate,
			values,
			extraSettings,
		}
	},
})
</script>

<style lang="scss" scoped>
.question__content {
	display: flex;
	flex-direction: column;
	gap: var(--default-grid-baseline);
}

.question__item {
	position: relative;
	display: inline-flex;
	min-height: var(--default-clickable-area);

	.question__input {
		width: calc(100% - var(--default-clickable-area));
		position: relative;
		inset-inline-start: -34px;
		inset-block-start: 1px;
		margin-inline-end: 10px !important;
		padding-inline-start: 36px !important;
	}

	.question__label {
		flex: 1 1 100%;
		// Overwrite guest page core styles
		text-align: start !important;
		// Some rounding issues lead to this strange number, so label and answerInput show up a the same position, working on different browsers.
		padding-block: 6.5px 0;
		padding-inline: 30px 0;
		line-height: 22px;
		min-height: 34px;
		height: min-content;
		position: relative;

		&::before {
			box-sizing: border-box;
			// Adjust position manually for proper position to text
			position: absolute;
			inset-block-start: 10px;
			width: 16px;
			height: 16px;
			margin-inline: -30px 14px !important;
			margin-block-end: 0;
		}
	}
}

.question__other-answer {
	display: flex;
	gap: 4px 16px;
	flex-wrap: wrap;

	.question__label {
		flex-basis: content;
	}

	.question__input {
		flex: 1;
		min-width: 260px;
	}

	.input-field__input {
		min-height: var(--default-clickable-area);
	}
}

.question__other-answer:deep() .input-field__input {
	min-height: var(--default-clickable-area);
}

.options-list-transition-move,
.options-list-transition-enter-active,
.options-list-transition-leave-active {
	transition: all var(--animation-slow) ease;
}

.options-list-transition-enter-from,
.options-list-transition-leave-to {
	opacity: 0;
	transform: translateX(var(--default-clickable-area));
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.options-list-transition-leave-active {
	position: absolute;
}

.answer-grid {
	border-collapse: collapse;
	width: 100%;

	thead tr {
		border-bottom: 2px solid var(--color-border);
	}

	td {
		min-height: 34px;
		min-width: 64px;
		text-align: center;
		padding: 8px 4px;

		.checkbox-radio-switch {
			display: flex;
			justify-content: center;
		}
	}

	th {
		min-height: 44px;
		padding: 8px 4px;
		text-align: center;
	}

	.first-column {
		min-width: 200px;
		text-align: start;
		position: sticky;
		inset-inline-start: 0;
	}
}
</style>
